import { Link } from "react-router";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  YAxis,
} from "recharts";

import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RoleplayRank } from "@/graphql";

interface ProgressionDataPoint {
  score: number;
  date: string;
  rank: RoleplayRank;
  scenarioSlug: string;
  sessionUuid: string;
}

interface ProgressionChartProps {
  data: ProgressionDataPoint[];
  className?: string;
}

// Custom tooltip component
const CustomTooltip = ({ data }: { data: ProgressionDataPoint }) => {
  return (
    <div
      className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg"
      style={{ pointerEvents: "auto" }}
    >
      <div className="text-sm text-gray-500">{data.date}</div>
      <div className="flex gap-2 items-center mt-1">
        <RankIcon rank={data.rank || RoleplayRank.Unranked} size={24} />
        <div className="text-xl text-black font-semibold">{data.score}%</div>
      </div>
      <Link to={`/scenarios/${data.scenarioSlug}/${data.sessionUuid}`}>
        <Button
          variant="outline"
          size="sm"
          className="mt-3 text-gray-700 border-gray-300 bg-gray-700/4 rounded-sm"
        >
          View Details
        </Button>
      </Link>
    </div>
  );
};

// Custom dot component for different ranks
const CustomDot = (props: {
  cx?: number;
  cy?: number;
  payload?: ProgressionDataPoint;
}) => {
  const { cx, cy, payload } = props;
  const rank = payload?.rank;

  if (!cx || !cy || !rank) return null;

  const getDotColor = (rank: RoleplayRank) => {
    switch (rank) {
      case RoleplayRank.Gold:
        return "url(#goldGradient)";
      case RoleplayRank.Silver:
        return "url(#silverGradient)";
      case RoleplayRank.Bronze:
        return "url(#bronzeGradient)";
      default:
        return "#FFFFFF";
    }
  };

  const getBorderColor = (rank: RoleplayRank) => {
    switch (rank) {
      case RoleplayRank.Gold:
        return "#FFD700";
      case RoleplayRank.Silver:
        return "#C0C0C0";
      case RoleplayRank.Bronze:
        return "#CD7F32";
      default:
        return "#CED5DE";
    }
  };
  return (
    // use custom tooltip for better ux (button click to view details) instead of recharts tooltip
    <Tooltip>
      <TooltipTrigger asChild>
        <circle
          className="cursor-pointer"
          cx={cx}
          cy={cy}
          r={6}
          fill={getDotColor(rank)}
          stroke={getBorderColor(rank)}
          strokeWidth={2}
        />
      </TooltipTrigger>
      {payload && (
        <TooltipContent
          className="bg-transparent p-0 shadow-sm"
          arrowClassName="hidden!"
        >
          <CustomTooltip data={payload} />
        </TooltipContent>
      )}
    </Tooltip>
  );
};

export function ProgressionChart({ data, className }: ProgressionChartProps) {
  return (
    <div className={`w-full h-48 ${className}`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 10, right: 10, left: -10, bottom: 10 }} // Increased left margin for more space
        >
          {/* Define gradients for different ranks */}
          <defs>
            <linearGradient
              id="goldGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#FFD700" />
              <stop offset="100%" stopColor="#FFA500" />
            </linearGradient>
            <linearGradient
              id="silverGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#C0C0C0" />
              <stop offset="100%" stopColor="#A9A9A9" />
            </linearGradient>
            <linearGradient
              id="bronzeGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#CD7F32" />
              <stop offset="100%" stopColor="#B8860B" />
            </linearGradient>
          </defs>

          {/* Grid and axes */}
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#F5F6F8"
            horizontal={true}
            vertical={false}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "#778397" }}
            tickFormatter={(value) => `${value}%`}
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickMargin={10} // Added tickMargin to space out Y-axis labels
          />

          {/* Area chart */}
          <Area
            type="monotone"
            dataKey="score"
            stroke="#CED5DE"
            strokeWidth={2}
            fill="none"
            dot={<CustomDot />}
            activeDot={{ r: 8, stroke: "#3B82F6", strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
